import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, MapPin } from "lucide-react";
import { PageHero, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import { BLOG_POSTS, BLOG_CATEGORIES } from "@/lib/blog";
import { useI18n } from "@/i18n";

export default function Blog() {
  const { t, localePath } = useI18n();
  const [cat, setCat] = useState("All");
  const posts = cat === "All" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === cat);
  const [featured, ...rest] = posts;

  return (
    <>
      <Seo
        title={t("blog.seoTitle")}
        description={t("blog.seoDesc")}
        path="/blog"
      />
      <PageHero crumb={t("blog.crumb")} eyebrow={t("blog.eyebrow")} title={t("blog.title")} highlight={t("blog.highlight")}
        subtitle={t("blog.subtitle")} />

      <section className="relative bg-[#FBFAF8] py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          {/* categories */}
          <div className="flex flex-wrap gap-2.5 mb-10">
            {BLOG_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} aria-pressed={cat === c} data-testid={`blog-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${cat === c ? "bg-[#0E3B3B] text-white" : "bg-white border border-[#E7E5DF] text-slate-600 hover:border-emerald-600/50 hover:text-emerald-700"}`}>
                {t(`cat.${c}`)}
              </button>
            ))}
          </div>

          {/* featured */}
          {featured && (
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-12">
              <Link to={localePath(`/blog/${featured.slug}`)} data-testid="blog-featured"
                className="group grid lg:grid-cols-2 gap-8 bg-white border border-[#E7E5DF] rounded-3xl overflow-hidden shadow-soft hover:border-emerald-600/40 transition-all">
                <div className="relative min-h-[260px] overflow-hidden">
                  <img loading="lazy" decoding="async" src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-emerald-500 text-[#0A2C2C] font-mono-tech text-[10px] uppercase tracking-widest">{t("blog.featured")}</span>
                </div>
                <div className="p-8 sm:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-4 font-mono-tech text-[11px] uppercase tracking-widest text-emerald-700 mb-4">
                    <span>{t(`cat.${featured.category}`)}</span>
                    <span className="flex items-center gap-1 text-slate-400"><MapPin size={12} /> {featured.city}</span>
                  </div>
                  <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-[#0B0F14] leading-tight group-hover:text-emerald-700 transition-colors">{featured.title}</h2>
                  <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 font-semibold text-[#0B0F14]">{t("blog.readArticle")} <ArrowUpRight size={18} /></span>
                </div>
              </Link>
            </motion.div>
          )}

          {/* grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.div key={p.slug} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }}>
                <Link to={localePath(`/blog/${p.slug}`)} data-testid={`blog-card-${p.slug}`}
                  className="group flex flex-col h-full bg-white border border-[#E7E5DF] rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:border-emerald-600/40 transition-all">
                  <div className="relative h-48 overflow-hidden">
                    <img loading="lazy" decoding="async" src={p.cover} alt={p.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur font-mono-tech text-[10px] uppercase tracking-wider text-[#0B0F14]">{t(`cat.${p.category}`)}</span>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 font-mono-tech text-[10px] uppercase tracking-widest text-slate-400 mb-3">
                      <span className="flex items-center gap-1 text-emerald-700"><MapPin size={11} /> {p.city}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> {p.read}</span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-[#0B0F14] leading-snug group-hover:text-emerald-700 transition-colors">{p.title}</h3>
                    <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B0F14]">{t("blog.readMore")} <ArrowUpRight size={15} /></span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
